import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import axios from "axios"
import moment from "moment"
import { SessionManager, showAccessDeniedAlert, showTokenExpiredAlert, getConfig } from "utils"

export const expenseList = createAsyncThunk("expense/list", async ({ pageable: data, navigate }) => {
    try {
        const res = await axios({
            method: "GET",
            url: getConfig.API_URL + "expenses",
            params: {
                pageSize: data?.pageSize,
                pageNo: data?.currentPage,
                search: data?.search,
                premise_id: data?.premise_id,
                user_id: data?.user_id,
                expense_type_id: data?.expense_type_id,
                from_date: data?.date_from && moment(data?.date_from, "DD/MM/YYYY").format("YYYY-MM-DD"),
                to_date: data?.date_to && moment(data?.date_to, "DD/MM/YYYY").format("YYYY-MM-DD"),
            },
            headers: { "Content-Type": "application/json", "Authorization": SessionManager.getCookie("user_token") },
        })
        return res
    } catch (error) {
        return error.response
    }
})

export const expenseDetails = createAsyncThunk("expense/details", async ({ id, navigate }) => {
    try {
        const res = await axios({
            method: "GET",
            url: getConfig.API_URL + `expenses/${id}`,
            headers: { "Content-Type": "application/json", "Authorization": SessionManager.getCookie("user_token") },
        })
        return res
    } catch (error) {
        return error.response
    }
})

export const addExpense = createAsyncThunk("expense/add", async (data) => {

    const bodyFormData = new FormData()

    bodyFormData.append("premise_id", data?.values?.premise_id ?? "")
    bodyFormData.append("expense_date", data?.values?.expense_date
        ? moment(data.values.expense_date, "DD/MM/YYYY").format("YYYY-MM-DD")
        : "")
    bodyFormData.append("user_type", data?.values?.user_type ?? "")
    bodyFormData.append("user_id", data?.values?.user_id ?? "")
    bodyFormData.append("expense_type_id", data?.values?.expense_type_id ?? "")
    bodyFormData.append("expense_amount", data?.values?.amount ?? "")
    bodyFormData.append("expense_narration", data?.values?.notes ?? "")
    if (data?.values?.invoice_file) {
        bodyFormData.append("expense_file", data.values.invoice_file)
    }

    try {
        const res = await axios({
            method: "POST",
            url: getConfig.API_URL + "expenses",
            data: bodyFormData,
            headers: { "Content-Type": "multipart/form-data", type: "expense_file", "Authorization": SessionManager.getCookie("user_token") },
        })
        return res
    } catch (error) {
        return error.response
    }
})

export const updateExpense = createAsyncThunk("expense/update", async (data) => {

    const bodyFormData = new FormData()

    bodyFormData.append("expenses_id", data?.values?.id)
    bodyFormData.append("premise_id", data?.values?.premise_id ?? "")
    bodyFormData.append("expense_date", data?.values?.expense_date
        ? moment(data.values.expense_date, "DD/MM/YYYY").format("YYYY-MM-DD")
        : "")
    bodyFormData.append("user_type", data?.values?.user_type ?? "")
    bodyFormData.append("user_id", data?.values?.user_id ?? "")
    bodyFormData.append("expense_type_id", data?.values?.expense_type_id ?? "")
    bodyFormData.append("expense_amount", data?.values?.amount ?? "")
    bodyFormData.append("expense_narration", data?.values?.notes ?? "")
    if (data?.values?.invoice_file) {
        bodyFormData.append("expense_file", data.values.invoice_file)
    }

    try {
        const res = await axios({
            method: "POST",
            url: getConfig.API_URL + "update_expenses",
            data: bodyFormData,
            headers: { "Content-Type": "multipart/form-data", type: "expense_file", "Authorization": SessionManager.getCookie("user_token") },
        })
        return res
    } catch (error) {
        return error.response
    }
})

export const addExpensePayment = createAsyncThunk("expense/addPayment", async (data) => {
    try {
        const res = await axios({
            method: "POST",
            url: getConfig.API_URL + "expense_payment",
            data: {
                expenses_id: data?.values?.expenses_id,
                payment_type: data?.values?.payment_type,
                payment_mode: data?.values?.payment_mode,
                paid_amount: data?.values?.amount,
                description: data?.values?.description,
            },
            headers: { "Content-Type": "application/json", "Authorization": SessionManager.getCookie("user_token") },
        })
        return res
    } catch (error) {
        return error.response
    }
})

export const deleteExpense = createAsyncThunk("expense/delete", async (data) => {
    try {
        const res = await axios({
            method: "DELETE",
            url: getConfig.API_URL + `expenses/${data?.id}`,
            headers: { "Content-Type": "application/json", "Authorization": SessionManager.getCookie("user_token") },
        })
        return res
    } catch (error) {
        return error.response
    }
})

const expense = createSlice({
    name: "expense",
    initialState: {
        list: [],
        details: null,
        loading: false,
    },
    extraReducers: (builder) => {
        builder.addCase(expenseList.pending, (state) => {
            state.loading = true
        })
        builder.addCase(expenseList.fulfilled, (state, action) => {
            state.loading = false
            if (action?.payload?.status === 200) {
                state.list = action.payload.data.data
            } else if (action?.payload?.status === 401) {
                showTokenExpiredAlert(action?.payload?.data?.message, action.meta.arg.navigate)
            } else if (action?.payload?.status === 403) {
                showAccessDeniedAlert(action.meta.arg.navigate)
            } else {
                state.list = []
            }
        })
        builder.addCase(expenseList.rejected, (state) => {
            state.loading = false
        })

        builder.addCase(expenseDetails.fulfilled, (state, action) => {
            if (action?.payload?.status === 200) {
                state.details = action.payload.data.data
            } else if (action?.payload?.status === 401) {
                showTokenExpiredAlert(action?.payload?.data?.message, action.meta.arg.navigate)
            } else if (action?.payload?.status === 403) {
                showAccessDeniedAlert(action.meta.arg.navigate)
            } else {
                state.details = null
            }
        })
    },
})

export default expense.reducer;